import * as React from 'react'

// Install global fetch interceptor for version checking (must be before any API calls)
import 'domains/application/appVersion'

import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import { Action } from 'history'
import { merge } from 'lodash'
import queryString, { ParsedQuery } from 'query-string'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { Provider } from 'react-redux'

import Application from 'domains/application/Application'
import { getHistory } from 'domains/application/hooks'
import slice from 'domains/application/slice'
import { getSocket } from 'domains/application/socket'
import { State, createStore } from 'domains/application/store'
import 'domains/application/i18n'
import { Source } from 'domains/organization/data-types'
import orgSlice from 'domains/organization/slice'
import authSlice from 'domains/auth/slice'
import vehicleSlice from 'domains/vehicle/slice'

window.__IS_SERVER__ = false

function createEmotionCache() {
  return createCache({ key: 'css' })
}

async function run() {
  const defaultState: Partial<State> = {
    auth: authSlice.getInitialState(),
    application: slice.getInitialState(),
    organization: orgSlice.getInitialState(),
    vehicle: vehicleSlice.getInitialState(),
  }
  const state = merge({}, defaultState, global.__APP__.state) as Partial<State>
  if (state.organization?.sources?.records != null) {
    state.organization.sources.recordsById =
      state.organization.sources.records.reduce(
        (acc, source) => {
          acc[source.id] = source
          return acc
        },
        {} as Record<string, Source>,
      )
  }

  const store = createStore(state, {
    devTools: window.__APP__.env === 'dev' || true,
    logActions: window.__APP__.env === 'dev',
  })

  if (__APP__.env === 'dev') {
    window.__STORE__ = store
  }
  window.__STORE__ = store

  const history = getHistory()
  window.__HISTORY__ = history

  const getUrlArgs = (): ParsedQuery => {
    return (
      queryString.parse(location.search, { arrayFormat: 'bracket' }) ??
      undefined
    )
  }

  // TODO: switch to history.block(...)
  history?.listen((update) => {
    const path = update.location.pathname
    store.dispatch(
      slice.actions.setPath({
        path,
        clearFlash: true,
        urlArgs: getUrlArgs(),
        action: update.action,
      }),
    )
  })

  store.dispatch(
    slice.actions.setPath({
      path: window.location.pathname,
      urlArgs: getUrlArgs(),
      action: Action.Push,
      clearFlash: false,
    }),
  )

  const socket = getSocket({
    _csrf_token: store.getState().application.csrfToken,
  })

  socket.onOpen(() => {
    console.log('Socket connected')
  })

  socket.connect()

  const cache = createEmotionCache()

  if (window.__APP__.ssr === true) {
    hydrateRoot(
      document.getElementById('app')!,
      <>
        <Provider store={store}>
          <CacheProvider value={cache}>
            <CssBaseline>
              <Application />
            </CssBaseline>
          </CacheProvider>
        </Provider>
      </>,
    )
  } else {
    const root = createRoot(document.getElementById('app')!)
    root.render(
      <>
        <Provider store={store}>
          <CacheProvider value={cache}>
            <CssBaseline>
              <Application />
            </CssBaseline>
          </CacheProvider>
        </Provider>
      </>,
    )
  }
}

run()
